import {openDeleteDialog} from 'platform/components';

import {useCallback, useMemo, useState} from 'react';

import {isNil} from 'ramda';

import i18n from '../../../../../i18n/i18n';
import {
  useCreateRelativeFilterMutation,
  useDeleteRelativeFilterMutation,
  useUpdateRelativeFilterMutation,
} from '../../../../../store/omneticApi';
import {RelativeFilterConfigBody, RelativeFilterResponseBody} from '../../../../../types/api/api';
import {handleApiError} from '../../../../../utils/handleApiError';
import {RelativeFilterPresetsController} from './useRelativeFilterPresetsController';

type PresetEditorDialogState =
  | {mode: 'closed'}
  | {mode: 'create'; initialConfig: RelativeFilterConfigBody | null}
  | {mode: 'edit'; presetId: string};

type UsePresetEditorOptions = {
  /** data-testid passed to the delete confirmation dialog. */
  deleteDialogTestId?: string;
};

/**
 * Shared open/save/delete logic of the saved-filter editor dialog. Used by the
 * sidebar SavedFilters dropdown and by the SavedFiltersPanel gear action.
 */
export function usePresetEditor(
  presetController: RelativeFilterPresetsController,
  options: UsePresetEditorOptions = {}
) {
  const {deleteDialogTestId} = options;
  const [createRelativeFilter] = useCreateRelativeFilterMutation();
  const [updateRelativeFilter] = useUpdateRelativeFilterMutation();
  const [deleteRelativeFilter] = useDeleteRelativeFilterMutation();
  const [dialogState, setDialogState] = useState<PresetEditorDialogState>({mode: 'closed'});
  // Bumped on every open so the dialog form remounts with fresh default values
  const [dialogInstanceKey, setDialogInstanceKey] = useState(0);

  const openCreate = useCallback((initialConfig: RelativeFilterConfigBody | null = null) => {
    setDialogInstanceKey((key) => key + 1);
    setDialogState({mode: 'create', initialConfig});
  }, []);

  const openEdit = useCallback((presetId: string) => {
    setDialogInstanceKey((key) => key + 1);
    setDialogState({mode: 'edit', presetId});
  }, []);

  const close = useCallback(() => {
    setDialogState({mode: 'closed'});
  }, []);

  const dialogPreset = useMemo((): RelativeFilterResponseBody | null => {
    if (dialogState.mode === 'edit') {
      return presetController.presets.find((preset) => preset.id === dialogState.presetId) ?? null;
    }
    if (dialogState.mode === 'create' && dialogState.initialConfig) {
      // Synthetic preset only prefills the editor form; saving still runs the create path
      return {
        id: '',
        name: '',
        config: dialogState.initialConfig,
        position: 0,
        system: false,
        default: false,
        createdAt: '',
        updatedAt: '',
      };
    }
    return null;
  }, [dialogState, presetController.presets]);

  const handleSave = useCallback(
    (name: string, config: RelativeFilterConfigBody, isDefault: boolean) => {
      if (isNil(presetController.customerId)) {
        return;
      }

      if (dialogState.mode === 'create') {
        createRelativeFilter({
          customerId: presetController.customerId,
          name,
          config,
          default: isDefault,
        })
          .unwrap()
          .then(({id}) => {
            presetController.setActivePresetId(id);
            presetController.applyPreset(config);
            setDialogState({mode: 'closed'});
          })
          .catch(handleApiError);
      } else if (dialogState.mode === 'edit') {
        const editedPresetId = dialogState.presetId;
        const editedPreset = presetController.presets.find(
          (preset) => preset.id === editedPresetId
        );
        const shouldApplyPresetAfterSave = presetController.activePresetId === editedPresetId;

        if (editedPreset?.system) {
          updateRelativeFilter({
            customerId: presetController.customerId,
            id: editedPresetId,
            default: isDefault,
          })
            .unwrap()
            .then(() => setDialogState({mode: 'closed'}))
            .catch(handleApiError);
        } else {
          updateRelativeFilter({
            customerId: presetController.customerId,
            id: editedPresetId,
            name,
            config,
            default: isDefault,
          })
            .unwrap()
            .then(() => {
              if (shouldApplyPresetAfterSave) {
                presetController.applyPreset(config);
              }
              setDialogState({mode: 'closed'});
            })
            .catch(handleApiError);
        }
      }
    },
    [dialogState, presetController, createRelativeFilter, updateRelativeFilter]
  );

  const deletePreset = useCallback(
    (presetId: string) => {
      if (isNil(presetController.customerId)) {
        return Promise.resolve();
      }
      return deleteRelativeFilter({id: presetId, customerId: presetController.customerId})
        .unwrap()
        .then(() => {
          presetController.setActivePresetId((current) => (current === presetId ? null : current));
          setDialogState({mode: 'closed'});
        })
        .catch(handleApiError);
    },
    [presetController, deleteRelativeFilter]
  );

  const handleDeletePress = useCallback(() => {
    if (dialogState.mode !== 'edit' || isNil(presetController.customerId)) {
      return;
    }
    const presetId = dialogState.presetId;
    const presetName =
      presetController.presets.find((preset) => preset.id === presetId)?.name ?? '';
    openDeleteDialog({
      id: 'price-report-delete-saved-filter-preset',
      text: i18n.t('entity.filterForm.savedFilters.dialog.deleteConfirm', {name: presetName}),
      onConfirm: () => deletePreset(presetId),
      'data-testid': deleteDialogTestId,
    });
  }, [dialogState, presetController, deletePreset, deleteDialogTestId]);

  return {
    dialogState,
    /** Remounts the dialog form whenever the dialog opens with different content. */
    dialogKey: `${dialogInstanceKey}-${dialogState.mode === 'edit' ? dialogState.presetId : dialogState.mode}`,
    isOpen: dialogState.mode !== 'closed',
    dialogPreset,
    openCreate,
    openEdit,
    close,
    handleSave,
    handleDeletePress,
  };
}

export type PresetEditor = ReturnType<typeof usePresetEditor>;
